<template>
  <el-card shadow="never" class="home-dashboard__card home-profit">
    <div slot="header" class="home-profit__header">
      <span class="home-dashboard__card-title" style="cursor: pointer" @click="toggle">{{ $t("resultado-financeiro") }}</span>
      <span class="home-profit__controls">
        <PeriodSwitch v-if="open" v-model="months" :options="monthOptions" @change="fetch" />
        <span class="home-profit__toggle" @click="toggle">
          {{ open ? $t("ocultar") : $t("exibir") }}
          <i :class="open ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </span>
      </span>
    </div>

    <div v-if="open" v-loading="loading" class="home-profit__body">
      <template v-if="data.monthly.length > 0">
        <div class="home-profit__kpis">
          <div class="home-profit__kpi home-profit__kpi--primary">
            <div class="home-profit__kpi-value">{{ currency(data.overall.profit) }}</div>
            <div class="home-profit__kpi-label">{{ $t("lucro") }} ({{ months }} meses)</div>
          </div>
          <div class="home-profit__kpi">
            <div class="home-profit__kpi-value">{{ currency(data.overall.revenue) }}</div>
            <div class="home-profit__kpi-label">Venda</div>
          </div>
          <div class="home-profit__kpi">
            <div class="home-profit__kpi-value">{{ currency(data.overall.cost) }}</div>
            <div class="home-profit__kpi-label">{{ $t("custo") }}</div>
          </div>
          <div class="home-profit__kpi">
            <div class="home-profit__kpi-value">{{ data.overall.margin }}%</div>
            <div class="home-profit__kpi-label">{{ $t("margem") }}</div>
          </div>
        </div>

        <e-chart :option="option" height="280px" />

        <div class="home-profit__ranks">
          <div v-if="data.topCustomers.length > 0" class="home-profit__rank-col">
            <div class="home-profit__rank-title">{{ $t("melhores-clientes") }}</div>
            <!-- Tooltip na LINHA inteira: o popup abre de qualquer ponto dela -->
            <el-tooltip v-for="row in data.topCustomers" :key="`top-${row.name}`" placement="top-start">
              <div slot="content" class="home-profit__tip">
                <b>{{ row.name }}</b>
                <div>{{ row.shipments.toLocaleString("pt-BR") }} envios</div>
                <div>Venda: {{ currency(row.revenue) }}</div>
                <div>Lucro: {{ currency(row.profit) }} ({{ row.margin }}%)</div>
                <div class="home-profit__tip-hint">clique para abrir a listagem</div>
              </div>
              <div class="home-profit__rank-row" :class="{ 'home-profit__rank-row--link': row.id }"
                @click="goCustomer(row)">
                <span class="home-profit__rank-name">{{ row.name }}</span>
                <span class="home-profit__rank-bar-track">
                  <span class="home-profit__rank-bar" :style="{ width: rankWidth(row, data.topCustomers) + '%' }"></span>
                </span>
                <span class="home-profit__rank-profit">{{ currency(row.profit) }}</span>
                <span class="home-profit__rank-margin">{{ row.margin }}%</span>
              </div>
            </el-tooltip>
          </div>

          <div v-if="data.worstCustomers.length > 0" class="home-profit__rank-col">
            <div class="home-profit__rank-title">{{ $t("clientes-atencao") }}</div>
            <el-tooltip v-for="row in data.worstCustomers" :key="`worst-${row.name}`" placement="top-start">
              <div slot="content" class="home-profit__tip">
                <b>{{ row.name }}</b>
                <div>{{ row.shipments.toLocaleString("pt-BR") }} envios</div>
                <div>Venda: {{ currency(row.revenue) }}</div>
                <div>Lucro: {{ currency(row.profit) }} ({{ row.margin }}%)</div>
                <div class="home-profit__tip-hint">clique para abrir a listagem</div>
              </div>
              <div class="home-profit__rank-row" :class="{ 'home-profit__rank-row--link': row.id }"
                @click="goCustomer(row)">
                <span class="home-profit__rank-name">{{ row.name }}</span>
                <span class="home-profit__rank-bar-track">
                  <span
                    class="home-profit__rank-bar home-profit__rank-bar--worst"
                    :class="{ 'home-profit__rank-bar--loss': row.profit < 0 }"
                    :style="{ width: rankWidth(row, data.worstCustomers) + '%' }"
                  ></span>
                </span>
                <span class="home-profit__rank-profit" :class="{ 'home-profit__rank-profit--loss': row.profit < 0 }">
                  {{ currency(row.profit) }}
                </span>
                <span class="home-profit__rank-margin">{{ row.margin }}%</span>
              </div>
            </el-tooltip>
          </div>
        </div>
      </template>
      <div v-else-if="!loading" class="home-profit__empty">
        {{ $t("sem-dados-financeiro") }}
      </div>
    </div>
  </el-card>
</template>

<script>
import moment from "moment";
import "moment/locale/pt-br";
import { DashboardFinancialService } from "@/services/customers/dashboard.financial.services";
import EChart from "@/components/Charts/EChart.vue";
import PeriodSwitch, { MONTH_OPTIONS } from "./PeriodSwitch.vue";

const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export default {
  name: "HomeProfitChart",
  components: { EChart, PeriodSwitch },
  data() {
    return {
      open: false,
      loading: false,
      fetched: false,
      months: 6,
      monthOptions: MONTH_OPTIONS,
      data: {
        monthly: [],
        overall: { revenue: 0, cost: 0, profit: 0, margin: 0 },
        topCustomers: [],
        worstCustomers: [],
      },
    };
  },
  computed: {
    option() {
      const labels = this.data.monthly.map((row) => {
        const [year, month] = row.month.split("-");
        return `${MONTHS_PT[+month - 1]}/${year.slice(2)}`;
      });
      return {
        tooltip: {
          trigger: "axis",
          valueFormatter: (value) => (typeof value === "number" && value > 100
            ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
            : `${value}`),
        },
        legend: {
          bottom: 0,
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: "#606266" },
        },
        // bottom reserva espaço para os rótulos do eixo X + respiro + legenda
        grid: { left: 72, right: 48, top: 16, bottom: 60, containLabel: false },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { lineStyle: { color: "#dcdfe6" } },
          axisTick: { show: false },
          axisLabel: { color: "#606266", fontSize: 11 },
        },
        yAxis: [
          {
            type: "value",
            splitLine: { lineStyle: { color: "#ebeef5" } },
            axisLabel: {
              color: "#909399",
              fontSize: 11,
              formatter: (value) => (value >= 1000 ? `${Math.round(value / 1000)}k` : value),
            },
          },
          {
            type: "value",
            min: 0,
            max: 100,
            splitLine: { show: false },
            axisLabel: { formatter: "{value}%", color: "#909399", fontSize: 11 },
          },
        ],
        series: [
          {
            name: "Venda",
            type: "bar",
            barMaxWidth: 22,
            itemStyle: { color: "#22418d" },
            data: this.data.monthly.map((row) => row.revenue),
          },
          {
            name: this.$t("custo"),
            type: "bar",
            barMaxWidth: 22,
            itemStyle: { color: "#909399" },
            data: this.data.monthly.map((row) => row.cost),
          },
          {
            name: this.$t("margem"),
            type: "line",
            yAxisIndex: 1,
            symbol: "circle",
            symbolSize: 6,
            itemStyle: { color: "#f6921e" },
            lineStyle: { color: "#f6921e", width: 2 },
            data: this.data.monthly.map((row) => row.margin),
          },
        ],
      };
    },
  },
  methods: {
    goCustomer(row) {
      if (!row.id) {
        return;
      }
      this.$router.push({
        name: "post",
        query: {
          tab: "post",
          byCreationDate: "0",
          customer: [row.id],
          startedAt: moment().subtract(this.months, "months").format("YYYY-MM-DD"),
          finishedAt: moment().format("YYYY-MM-DD"),
        },
      });
    },
    currency(value) {
      return (+value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    rankWidth(row, list) {
      const max = list.reduce((acc, item) => Math.max(acc, Math.abs(item.profit)), 0);
      if (!max) {
        return 0;
      }
      return Math.max(4, Math.round((Math.abs(row.profit) / max) * 100));
    },
    toggle() {
      this.open = !this.open;
      if (this.open && !this.fetched) {
        this.fetch();
      }
    },
    fetch() {
      const vm = this;
      vm.loading = true;
      DashboardFinancialService.profit(vm.months)
        .then((response) => {
          vm.data = { ...vm.data, ...response };
          vm.fetched = true;
        })
        .catch((error) => {
          vm.$message({ type: "error", message: error });
        })
        .finally(() => {
          vm.loading = false;
        });
    },
  },
};
</script>

<style>
.home-profit__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.home-profit__controls {
  display: flex;
  align-items: center;
}

.home-profit__toggle {
  font-size: 12px;
  color: var(--me-text-secondary);
  cursor: pointer;
  margin-left: 14px;
}

.home-profit__kpis {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.home-profit__kpi {
  flex: 1 1 140px;
  padding: 8px 14px 8px 0;
}

.home-profit__kpi-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--me-text-primary);
  font-variant-numeric: tabular-nums;
}

.home-profit__kpi--primary .home-profit__kpi-value {
  color: var(--me-primary);
  font-size: 22px;
}

.home-profit__kpi-label {
  font-size: 12px;
  color: var(--me-text-secondary);
}

.home-profit__ranks {
  display: flex;
  flex-wrap: wrap;
}

.home-profit__rank-col {
  flex: 1 1 340px;
  min-width: 300px;
  margin-right: 20px;
}

.home-profit__rank-col:last-child {
  margin-right: 0;
}

.home-profit__rank-bar--worst {
  background: #c0c4cc;
}

.home-profit__rank-bar--loss {
  background: var(--me-danger);
}

.home-profit__rank-profit--loss {
  color: var(--me-danger);
  font-weight: 600;
}

.home-profit__rank-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--me-text-secondary);
  margin: 12px 0 6px;
}

.home-profit__tip {
  line-height: 1.6;
}

.home-profit__tip-hint {
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.7;
}

.home-profit__rank-row--link {
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.home-profit__rank-row--link:hover {
  background-color: var(--me-fill);
}

.home-profit__rank-row--link:hover {
  background-color: var(--me-fill);
}

.home-profit__rank-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.home-profit__rank-name {
  width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--me-text-regular);
  font-weight: 600;
}

.home-profit__rank-bar-track {
  flex: 1;
  height: 6px;
  background: var(--me-fill);
  border-radius: 2px;
  margin: 0 8px;
  overflow: hidden;
}

.home-profit__rank-bar {
  display: block;
  height: 100%;
  background: var(--me-primary);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.home-profit__rank-profit {
  min-width: 110px;
  text-align: right;
  color: var(--me-text-regular);
  font-variant-numeric: tabular-nums;
}

.home-profit__rank-margin {
  min-width: 52px;
  text-align: right;
  color: var(--me-text-secondary);
  font-variant-numeric: tabular-nums;
}

.home-profit__empty {
  color: var(--me-text-secondary);
  font-size: 13px;
  padding: 8px 0;
}
</style>
